import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { CrossIcon } from 'src/svgs/Viagogo';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  SectionInfo,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import * as styles from './CampaignModal.css';
import { ZoneSelectorPanel } from './ZoneSelectorPanel';

export const ZoneTargetingMapPanel = ({
  disabled,
  onClose,
}: {
  disabled: boolean;
  onClose?: () => void;
}) => {
  const { venueMapInfo } = useEventMapContext();
  const { watch, setValue, getValues } =
    useFormContext<SponsoredListingsCampaignInput>();
  const targetTicketClassIds = watch('targetTicketClassIds');
  const seatMapRef = useRef<HTMLDivElement>(null);

  const [hoveredTicketClassId, setHoveredTicketClassId] = useState<
    number | null
  >(null);

  const selectedSectionIds = useMemo(() => {
    const selectedTcIds = targetTicketClassIds?.value ?? [];
    if (!selectedTcIds.length || !venueMapInfo?.sections) return undefined;
    return venueMapInfo.sections
      .filter((s) =>
        s.rows?.some(
          (r) => r.tktClass?.id != null && selectedTcIds.includes(r.tktClass.id)
        )
      )
      .map((s) => s.id);
  }, [targetTicketClassIds, venueMapInfo]);

  const getColor = useCallback(
    (info: {
      sectionId: number;
      ticketClassId: number;
      isSelected: boolean;
      isMarked?: boolean;
    }) => {
      if (info.isSelected) {
        return {
          stroke: vars.color.borderActive,
          fill: vars.color.backgroundHighlight,
          textColor: vars.color.textStrong,
        };
      }
      // Let showDefaultMapColors handle default coloring
      return { fill: '', stroke: '' };
    },
    []
  );

  // Mimic native hover effect (fill-opacity) for zone selector hover
  useEffect(() => {
    if (!hoveredTicketClassId || !venueMapInfo?.sections || !seatMapRef.current)
      return;

    const sectionIds = venueMapInfo.sections
      .filter((s) =>
        s.rows?.some((r) => r.tktClass?.id === hoveredTicketClassId)
      )
      .map((s) => s.id);

    const paths: SVGElement[] = [];
    sectionIds.forEach((id) => {
      const path = seatMapRef.current?.querySelector(`path[eid="${id}"]`);
      if (path) paths.push(path as SVGElement);
    });

    paths.forEach((p) => p.setAttribute('fill-opacity', '0.25'));

    return () => {
      paths.forEach((p) => p.setAttribute('fill-opacity', '1'));
    };
  }, [hoveredTicketClassId, venueMapInfo]);

  const handleSectionClicked = useCallback(
    (_e: MouseEvent, selectedSection: SectionInfo) => {
      if (disabled) return;
      const ticketClassId = selectedSection.rows
        ?.map((r) => r.tktClass?.id)
        .find((id) => id != null);
      if (ticketClassId == null) return;

      const currentIds = getValues('targetTicketClassIds')?.value ?? [];
      const isCurrentlySelected = currentIds.includes(ticketClassId);
      const newIds = isCurrentlySelected
        ? currentIds.filter((id) => id !== ticketClassId)
        : [...currentIds, ticketClassId];
      setValue(
        'targetTicketClassIds',
        posChangedField(newIds.length > 0 ? newIds : [])
      );
    },
    [disabled, getValues, setValue]
  );

  return (
    <>
      <div ref={seatMapRef} className={styles.seatMapPanel}>
        {onClose && (
          <button onClick={onClose} className={styles.mapCloseButton}>
            <CrossIcon size={vars.iconSize.m} />
            Close
          </button>
        )}
        <EventVenueMap
          showDefaultMapColors
          hideControls={false}
          isZoomEnabled
          isPanEnabled
          isCondensedView
          onSectionClicked={handleSectionClicked}
          selectedSectionIds={selectedSectionIds}
          getColor={getColor}
          mapActionContainerClassname={styles.mapControlsBottomRight}
        />
      </div>
      <ZoneSelectorPanel
        disabled={disabled}
        onHoverTicketClass={setHoveredTicketClassId}
      />
    </>
  );
};
